import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import ProtectedLink from '../components/ProtectedLink';
import { useContent } from '../hooks/useContent';

const icons = {
  '4-squares': (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h6v6H4zM14 5h6v6h-6zM4 15h6v6H4zM14 15h6v6h-6z" />
    </svg>
  ),
  'interlocked-links': (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  ),
  'bar-graph': (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  'light-bulb': (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  ),
};

const Product = () => {
  const { content, loading: contentLoading } = useContent('product');
  const { isDark } = useTheme();
  if (contentLoading || !content) {
    return (
      <div className={`pt-16 min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  return (
    <div className={`pt-16 min-h-screen ${
      isDark ? 'bg-dark-900' : 'bg-gray-50'
    }`}>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-primary-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-secondary-500 rounded-full filter blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 rounded-full border border-primary-500/30 mb-8">
            <svg className="w-5 h-5 text-primary-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span className="text-primary-400 font-medium">{content.hero.badge}</span>
          </div>
          <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
	    {content.hero.title}
            <span className="block bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
		{content.hero.titleHighlight}
            </span>
          </h1>
          <p className={`text-lg sm:text-xl max-w-4xl mx-auto leading-relaxed ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
	    {content.hero.subtitle}
	  </p>
        </div>
      </section>

      <section className={`py-20 ${
        isDark 
          ? 'bg-gradient-to-b from-dark-900 to-dark-800' 
          : 'bg-gradient-to-b from-white to-gray-100'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {content.products.map((product, index) => (
              <div key={product.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="mb-6">
                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${product.gradient} rounded-2xl mb-6`}>
		    <div className="text-white">
		    {icons[product.icon] ?? <span className="w-8 h-8 block" />} {}  
		    </div>
                    </div>
                    <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {product.title}
                    </h2>
                    <p className="text-xl text-primary-400 font-medium mb-6">
                      {product.subtitle}
                    </p>
                    <p className={`text-lg leading-relaxed mb-8 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {product.description}
                    </p>
                  </div>

                  <div className="space-y-4 mb-8">
                    {product.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <div className={`w-2 h-2 bg-gradient-to-r ${product.gradient} rounded-full`}></div>
                        <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <ProtectedLink 
                    to={product.link}
                    className={`inline-flex items-center px-6 py-3 bg-gradient-to-r ${product.gradient} hover:opacity-90 text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-105`}
                  >
                    {product.linkText}
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </ProtectedLink>
                </div>

                <div className={`relative ${
                  index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''
                }`}>
                  <div className={`rounded-2xl p-8 border ${
                    isDark 
                      ? 'bg-gradient-to-br from-dark-700 to-dark-800 border-dark-600' 
                      : 'bg-gradient-to-br from-white to-gray-50 border-gray-200 shadow-lg'
                  }`}>
                    {product.id === 'qr-codes' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className={`rounded-xl p-2 aspect-square flex items-center justify-center ${
                          isDark ? 'bg-dark-900' : 'bg-gray-100'
                        }`}>
                          <img 
                            src="/images/qr-ai-example-2.png" 
                            alt="QR Code classique" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className={`rounded-xl p-2 aspect-square flex items-center justify-center ${
                          isDark ? 'bg-dark-900' : 'bg-gray-100'
                        }`}>
                          <img 
                            src="/images/qr-ai-example-3.png" 
                            alt="QR Code artistique" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className={`rounded-xl p-2 aspect-square flex items-center justify-center ${
                          isDark ? 'bg-dark-900' : 'bg-gray-100'
                        }`}>
                          <img 
                            src="/images/qr-ai-example-4.png" 
                            alt="QR Code dynamique" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className={`rounded-xl p-2 aspect-square flex items-center justify-center ${
                          isDark ? 'bg-dark-900' : 'bg-gray-100'
                        }`}>
                          <img 
                            src="/images/qr-ai-example-1.png" 
                            alt="QR Code personnalisé" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>
                    )}

                    {product.id === 'links' && (
                      <div className="space-y-4">
                        <div className={`rounded-lg p-4 flex items-center justify-between ${
                          isDark ? 'bg-dark-900' : 'bg-gray-100'
                        }`}>
                          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>https://example.com/very-long-url...</span>
                          <span className="text-secondary-400 font-mono">qkr.ly/abc123</span>
                        </div>
                        <div className={`rounded-lg p-4 flex items-center justify-between ${
                          isDark ? 'bg-dark-900' : 'bg-gray-100'
                        }`}>
                          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>GS1 Digital Link</span>
                          <span className="text-secondary-400 font-mono">id.gs1.org/01/...</span>
                        </div>
                      </div>
                    )}

                    {product.id === 'analytics' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div className={`rounded-lg p-4 text-center ${
                            isDark ? 'bg-dark-900' : 'bg-gray-100'
                          }`}>
                            <div className="text-2xl font-bold text-primary-400 mb-1">1.2K</div>
                            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Scans</div>
                          </div>
                          <div className={`rounded-lg p-4 text-center ${
                            isDark ? 'bg-dark-900' : 'bg-gray-100'
                          }`}>
                            <div className="text-2xl font-bold text-secondary-400 mb-1">89%</div>
                            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Mobile</div>
                          </div>
                          <div className={`rounded-lg p-4 text-center ${
                            isDark ? 'bg-dark-900' : 'bg-gray-100'
                          }`}>
                            <div className="text-2xl font-bold text-primary-400 mb-1">24</div>
                            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pays</div>
                          </div>
                        </div>
                        <div className={`rounded-lg p-4 ${
                          isDark ? 'bg-dark-900' : 'bg-gray-100'
                        }`}>
                          <div className="flex items-end space-x-2 h-16">
                            {[40, 65, 30, 80, 45, 70, 55].map((height, i) => (
                              <div key={i} className={`bg-gradient-to-t from-primary-500 to-secondary-500 rounded-t flex-1`} style={{height: `${height}%`}}></div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {product.id === 'ai' && (
                      <div className="text-center">
                        <div className={`rounded-xl p-4 mb-4 ${
                          isDark ? 'bg-dark-900' : 'bg-gray-100'
                        }`}>
                          <img 
                            src="/images/qr-ai-example-1.png" 
                            alt="QR Code généré par IA" 
                            className="w-90 h-90 mx-auto mb-4 object-contain"
                          />
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>"QuikR code futuriste avec des lignes néon"</p>
                        </div>
                        <div className="flex justify-center space-x-2">
                          <div className="w-3 h-3 bg-primary-500 rounded-full animate-pulse"></div>
                          <div className="w-3 h-3 bg-secondary-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                          <div className="w-3 h-3 bg-primary-400 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={`absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-r ${product.gradient} rounded-full opacity-20 blur-2xl`}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`py-20 ${
        isDark 
          ? 'bg-gradient-to-b from-dark-800 to-dark-900' 
          : 'bg-gradient-to-b from-gray-100 to-white'
      }`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`text-3xl sm:text-4xl font-bold mb-6 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
	  {content.cta.title}
            <span className="block bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
	  	{content.cta.titleHighlight}
            </span>
          </h2>
          <p className={`text-lg mb-8 max-w-2xl mx-auto ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
		{content.cta.subtitle}  
	  </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <ProtectedLink 
              to="/dashboard" 
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-105"
            >
		  {content.cta.primaryButton}
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </ProtectedLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Product;
